<template>
  <div>
    <v-row class="mt-3">
      <v-col cols="12" md="3">
        <div class="body-small">Volume since {{ startLabel }}</div>
        <div class="text-h6">{{ formatPrice(volume) }}</div>
      </v-col>
      <v-col cols="12" md="3">
        <div class="body-small">Current commission</div>
        <div class="text-h6">{{ percent(currentTier.rate) }}</div>
      </v-col>
      <v-col cols="12" md="6">
        <div class="body-small">Next tier</div>
        <div class="text-h6" v-if="nextTier">
          {{ percent(nextTier.rate) }} from {{ formatPrice(nextTier.from) }}
          <span class="body-small">
            — {{ formatPrice(nextTier.from - volume) }} to go</span
          >
        </div>
        <div class="text-h6" v-else>Top tier reached</div>
      </v-col>
    </v-row>

    <h3 class="mt-6 mb-2">Tiers</h3>
    <v-simple-table dense class="tiers-table">
      <thead>
        <tr>
          <th>From</th>
          <th>To</th>
          <th>Commission</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(tier, index) in tiers" :key="tier.from">
          <td>{{ formatPrice(tier.from) }}</td>
          <td>
            {{
              tiers[index + 1] ? formatPrice(tiers[index + 1].from) : "and above"
            }}
          </td>
          <td>{{ percent(tier.rate) }}</td>
          <td>
            <v-chip x-small color="success" v-if="tier.from == currentTier.from"
              >current</v-chip
            >
            <span class="body-small" v-else-if="tier.from < currentTier.from"
              >reached</span
            >
          </td>
        </tr>
      </tbody>
    </v-simple-table>

    <h3 class="mt-6 mb-2">Breakdown by commission</h3>
    <p class="body-small mb-2">
      Every commission keeps the rate it was earned at. Expand a row to see the
      clients.
    </p>
    <v-data-table
      :headers="headers"
      :items="groups"
      :loading="loading"
      item-key="rate"
      show-expand
      single-expand
      :expanded.sync="expanded"
      hide-default-footer
      disable-pagination
      class="elevation-0"
    >
      <template v-slot:item.rate="{ value }">{{ value }}%</template>
      <template v-slot:item.volume="{ value }">{{ formatPrice(value) }}</template>
      <template v-slot:item.commission="{ value }">{{
        formatPrice(value)
      }}</template>
      <template v-slot:item.paid="{ value }">{{ formatPrice(value) }}</template>
      <template v-slot:item.owed="{ value }">{{ formatPrice(value) }}</template>
      <template v-slot:expanded-item="{ headers, item }">
        <td :colspan="headers.length" class="pl-12 pr-0 py-2">
          <v-simple-table dense>
            <thead>
              <tr>
                <th>Client</th>
                <th>Payments</th>
                <th>Billed</th>
                <th>Commission</th>
                <th>Paid</th>
                <th>Owed</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="client in item.clients" :key="client.id">
                <td>{{ client.name }}</td>
                <td>{{ client.count }}</td>
                <td>{{ formatPrice(client.volume) }}</td>
                <td>{{ formatPrice(client.commission) }}</td>
                <td>{{ formatPrice(client.paid) }}</td>
                <td>{{ formatPrice(client.owed) }}</td>
              </tr>
            </tbody>
          </v-simple-table>
        </td>
      </template>
      <template v-slot:body.append v-if="groups.length > 1">
        <tr class="font-weight-bold">
          <td>Total</td>
          <td>{{ totals.count }}</td>
          <td>{{ totals.clients }}</td>
          <td>{{ formatPrice(totals.volume) }}</td>
          <td>{{ formatPrice(totals.commission) }}</td>
          <td>{{ formatPrice(totals.paid) }}</td>
          <td>{{ formatPrice(totals.owed) }}</td>
          <td></td>
        </tr>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import {getDocs, query, where} from "firebase/firestore";
import {colCommissions} from "@/assets/utils/firebase.utils";
import {mapDocs} from "@/assets/utils/doc.utils";
import moment from "moment-timezone";

const sum = (list, key) =>
  parseFloat(list.reduce((t, i) => t + Number(i[key] || 0), 0).toFixed(2));

export default {
  props: {
    affiliate: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      loading: false,
      expanded: [],
      commissions: [],
      headers: [
        { text: "Commission", value: "rate" },
        { text: "Payments", value: "count" },
        { text: "Clients", value: "clientCount" },
        { text: "Billed", value: "volume" },
        { text: "Commission earned", value: "commission" },
        { text: "Paid", value: "paid" },
        { text: "Owed", value: "owed" },
        { text: "", value: "data-table-expand" },
      ],
    };
  },
  computed: {
    tiers() {
      return [...(this.affiliate.commission_tiers || [])].sort(
        (a, b) => a.from - b.from
      );
    },
    // Same sum the server uses for the rate (utils/commission.tiers.js).
    volume() {
      return sum(this.commissions, "amount");
    },
    currentTier() {
      let current = this.tiers[0] || { from: 0, rate: 0 };
      for (let tier of this.tiers) {
        if (this.volume >= tier.from) current = tier;
      }
      return current;
    },
    nextTier() {
      return this.tiers.find((i) => i.from > this.volume) || null;
    },
    startLabel() {
      let start = this.affiliate.commission_tiers_start;
      return start ? moment(start).format("MMM D, YYYY") : "start";
    },
    groups() {
      let byRate = {};
      for (let item of this.commissions) {
        let rate = parseFloat(Number(item.commission_rate || 0).toFixed(2));
        (byRate[rate] = byRate[rate] || []).push(item);
      }
      return Object.keys(byRate)
        .map((rate) => this.summarize(byRate[rate], parseFloat(rate)))
        .sort((a, b) => a.rate - b.rate);
    },
    totals() {
      return {
        ...this.summarize(this.commissions, null),
        clients: new Set(this.commissions.map((i) => i.customer_id)).size,
      };
    },
  },
  methods: {
    percent(rate) {
      return `${parseFloat((Number(rate || 0) * 100).toFixed(2))}%`;
    },
    summarize(list, rate) {
      let paidList = list.filter((i) => i.payout === true);
      let byClient = {};
      for (let item of list) {
        let key = item.customer_id || item.email || "unknown";
        (byClient[key] = byClient[key] || []).push(item);
      }
      let clients = Object.keys(byClient)
        .map((id) => {
          let items = byClient[id];
          let paid = sum(
            items.filter((i) => i.payout === true),
            "commission"
          );
          let commission = sum(items, "commission");
          return {
            id,
            name: items[0].name || items[0].email || id,
            count: items.length,
            volume: sum(items, "amount"),
            commission,
            paid,
            owed: parseFloat((commission - paid).toFixed(2)),
          };
        })
        .sort((a, b) => b.volume - a.volume);
      let commission = sum(list, "commission");
      let paid = sum(paidList, "commission");
      return {
        rate,
        count: list.length,
        clientCount: clients.length,
        clients,
        volume: sum(list, "amount"),
        commission,
        paid,
        owed: parseFloat((commission - paid).toFixed(2)),
      };
    },
    async fetchCommissions() {
      if (!this.affiliate.id) return;
      try {
        this.loading = true;
        // tier_counted is set by the onCommissionWrite trigger for commissions
        // dated on or after the tiers start.
        let q = query(
          colCommissions,
          where("affiliate_id", "==", this.affiliate.id),
          where("tier_counted", "==", true)
        );
        this.commissions = mapDocs((await getDocs(q)).docs);
        this.loading = false;
      } catch (error) {
        this.loading = false;
        this.handleError(error);
      }
    },
  },
  watch: {
    "affiliate.id"() {
      this.fetchCommissions();
    },
  },
  mounted() {
    this.fetchCommissions();
  },
};
</script>

<style scoped>
.tiers-table {
  max-width: 600px;
}
</style>
